import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { resolveImage } from '../components/admin/ImageUpload';
import { Loader2, Package, ChevronRight, RotateCcw, Eye } from 'lucide-react';
import { toast } from 'sonner';
import OrderDetailDialog from '../components/OrderDetailDialog';

const STATUS_COLORS = {
  pending: 'bg-neutral-100 text-neutral-700',
  confirmed: 'bg-blue-100 text-blue-700',
  shipped: 'bg-orange-100 text-orange-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const OrdersPage = () => {
  const { user, loading: authLoading } = useAuth();
  const cart = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reorderingId, setReorderingId] = useState('');
  const [detailOrder, setDetailOrder] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get('/orders').then(res => setOrders(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const reorder = async (orderId) => {
    setReorderingId(orderId);
    try {
      await api.post(`/orders/${orderId}/reorder`);
      // Pull the fresh server cart into React context BEFORE navigating,
      // otherwise CartPage renders the stale (empty) local state.
      await cart?.refresh?.();
      toast.success('Items added to basket');
      setDetailOrder(null);
      navigate('/cart');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reorder failed');
    } finally { setReorderingId(''); }
  };

  if (authLoading || loading) return <div className="max-w-[1400px] mx-auto px-4 py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-[#12263A] mb-4">Sign in to see your orders</h1>
        <Link to="/login?redirect=/orders" className="inline-block bg-[#F46A25] text-white font-bold px-6 py-3 rounded-sm hover:bg-[#e05a15]">Sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      <nav className="flex items-center gap-2 text-xs text-neutral-600 mb-4">
        <Link to="/" className="hover:text-[#F46A25]">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-[#12263A] font-semibold">My orders</span>
      </nav>
      <h1 className="text-3xl md:text-4xl font-black text-[#12263A] mb-6">My orders</h1>

      {orders.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-sm p-12 text-center">
          <Package className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-600 mb-4">You haven't placed any orders yet.</p>
          <Link to="/" className="inline-block bg-[#F46A25] text-white font-bold px-6 py-3 rounded-sm hover:bg-[#e05a15]">Start shopping</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(o => (
            <div key={o.id} className="bg-white border border-neutral-200 rounded-sm p-4 hover:border-[#F46A25] transition-colors" data-testid={`order-row-${o.id}`}>
              <div className="flex flex-wrap items-center gap-3 justify-between mb-3 pb-3 border-b border-neutral-100">
                <div>
                  <div className="text-xs text-neutral-500">Order</div>
                  <button
                    type="button"
                    onClick={() => setDetailOrder(o)}
                    data-testid={`open-order-${o.id}`}
                    className="font-mono font-bold text-[#12263A] hover:text-[#F46A25] underline underline-offset-2"
                    title="View order details"
                  >#{o.id.slice(0, 8)}</button>
                </div>
                <div>
                  <div className="text-xs text-neutral-500">Date</div>
                  <div className="text-sm font-semibold text-[#12263A]">{new Date(o.created_at).toLocaleDateString('en-GB')}</div>
                </div>
                <div>
                  <div className="text-xs text-neutral-500">Total</div>
                  <div className="text-sm font-bold text-[#12263A]">£{o.total.toFixed(2)}</div>
                </div>
                <div>
                  <span className={`text-xs font-bold uppercase px-2.5 py-1 rounded-sm ${STATUS_COLORS[o.status] || 'bg-neutral-100'}`}>{o.status}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDetailOrder(o)}
                    data-testid={`view-order-${o.id}`}
                    className="text-xs bg-white border border-neutral-300 hover:border-[#F46A25] text-[#12263A] font-bold px-3 py-1.5 rounded-sm flex items-center gap-1"
                  ><Eye className="w-3 h-3" /> View</button>
                  <button
                    onClick={() => reorder(o.id)}
                    disabled={reorderingId === o.id}
                    className="text-xs bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-3 py-1.5 rounded-sm flex items-center gap-1 disabled:opacity-60"
                  >
                    {reorderingId === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    Reorder
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {o.items.slice(0, 5).map(i => (
                  <Link key={i.product_id} to={`/product/${i.product_id}`} className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-sm p-2 hover:border-[#F46A25]">
                    <img src={resolveImage(i.image)} alt={i.name} className="w-12 h-12 object-cover rounded-sm" />
                    <div className="text-xs">
                      <div className="font-semibold text-[#12263A] line-clamp-1 max-w-[180px]">{i.name}</div>
                      <div className="text-neutral-600">Qty {i.qty} • £{i.price.toFixed(2)}</div>
                    </div>
                  </Link>
                ))}
                {o.items.length > 5 && <span className="text-xs text-neutral-500 self-center">+{o.items.length - 5} more</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <OrderDetailDialog
        open={!!detailOrder}
        order={detailOrder}
        onClose={() => setDetailOrder(null)}
        onReorder={reorder}
        reordering={reorderingId === detailOrder?.id}
      />
    </div>
  );
};

export default OrdersPage;
